<template>
	<div v-if="!makeHidden || (makeHidden && mainPage)" class="flex w-full items-center px-1">
		<div
			v-if="!makeHidden"
			class="text-12 z-1 w-auto flex-1 cursor-pointer whitespace-nowrap py-1.5 px-2 text-center font-bold duration-300 ease-in-out max-[374px]:text-[10px] lg:w-1/3 lg:text-xl"
			:class="{
				'text-main-400 border-main-400 border-b-2': formType === 'ymm',
				'text-secondary border-b-2 border-gray-300': formType !== 'ymm',
			}"
			@click="onChangeTab('ymm')"
		>
			MAKE & MODEL
		</div>

		<div
			class="text-12 z-1 w-auto flex-1 cursor-pointer whitespace-nowrap py-1.5 px-2 text-center font-bold duration-300 ease-in-out max-[374px]:text-[10px] lg:w-1/3 lg:text-xl"
			:class="{
				'text-main-400 border-main-400 border-b-2': formType === 'vin',
				'text-secondary border-b-2 border-gray-300': formType !== 'vin',
			}"
			@click="onChangeTab('vin')"
		>
			VIN
		</div>

		<div
			class="text-12 z-1 w-auto flex-1 cursor-pointer whitespace-nowrap py-1.5 px-2 text-center font-bold duration-300 ease-in-out max-[374px]:text-[10px] lg:w-1/3 lg:text-xl"
			:class="{
				'text-main-400 border-main-400 border-b-2': formType === 'lp',
				'text-secondary border-b-2 border-gray-300': formType !== 'lp',
			}"
			@click="onChangeTab('lp')"
		>
			LICENSE PLATE
		</div>
	</div>

	<div v-if="!makeHidden || mainPage" class="tall:xl:mb-12 mb-4 md:mb-6 lg:mb-8" />

	<div class="flex grow flex-col justify-between" :class="{ 'h-full': inHeader }">
		<form
			v-if="formType === 'ymm' && !makeHidden"
			class="flex flex-col"
			action="#"
			@submit.prevent
		>
			<div class="app-input-wrapper">
				<select
					v-model="form.year"
					class="app-input app-input-select mb-3 grow"
					:class="{ 'border-error bg-error/10': validate && !isYearValid }"
					name="year"
					@change="onChangeYear"
				>
					<option v-for="year in form.getYears" :key="year" :disabled="year === 'Year'">
						{{ year }}
					</option>
				</select>
				<div
					class="text-main-400 pointer-events-none absolute top-5 right-8 h-1 w-1 scale-y-50 lg:top-6"
				>
					▼
				</div>
			</div>

			<div class="app-input-wrapper">
				<select
					v-model="form.make"
					:disabled="isMakeDisabled"
					class="app-input app-input-select mb-3 grow"
					:class="{
						'border-error bg-error/10': validate && !isMakeDisabled && !isMakeValid,
					}"
					name="make"
					@change="onChangeMake"
				>
					<option v-for="make in form.getMakes" :key="make.name">
						{{ make.name }}
					</option>
				</select>
				<div
					class="text-main-400 pointer-events-none absolute top-5 right-8 h-1 w-1 scale-y-50 lg:top-6"
				>
					▼
				</div>
			</div>

			<div class="app-input-wrapper">
				<select
					v-model="form.model"
					:disabled="isModelDisabled"
					class="app-input app-input-select mb-3 grow"
					:class="{
						'border-error bg-error/10': validate && !isModelDisabled && !isModelValid,
					}"
					name="model"
					@change="onChangeModel"
				>
					<option v-for="model in form.getModels" :key="model.name">
						{{ model.name }}
					</option>
				</select>
				<div
					class="text-main-400 pointer-events-none absolute top-5 right-8 h-1 w-1 scale-y-50 lg:top-6"
				>
					▼
				</div>
			</div>
		</form>

		<form
			v-if="
				(formType === 'vin' && !makeHidden) ||
				(makeHidden && mainPage && formType === 'vin')
			"
			class="flex flex-col"
			action="#"
			@submit.prevent
		>
			<AppInput
				v-model="form.vin"
				v-maska="vinMask"
				class="mt-1 mb-4"
				:input-class="validate && !isVinValid ? 'bg-error/10 border-error' : ''"
				name="VIN"
				placeholder="Enter your VIN"
				@input="onInputVin"
				@submit="onSubmit"
			>
				<template #append>
					<Tooltip
						class="xs:-right-3 absolute -top-1 -right-2 h-6 w-6"
						text-class="text-secondary -translate-y-[56%]"
						position="left"
					>
						<template #default>
							<HelpSvg class="fill-secondary" />
						</template>

						<template #content>
							<p class="text-secondary text-12 text-left font-bold">
								Where can I find my VIN?
							</p>

							<p class="text-secondary text-12 mb-2 text-left">
								You can find your VIN in the vehicle registration, title, or your
								insurance card. The VIN number can also be found on the inside of
								the driver’s door or on the driver’s side of the dashboard.
							</p>

							<p class="text-secondary text-12 mb-2 text-left">
								- A VIN cannot include letters
								<span class="font-bold">I, O, Q</span>
							</p>

							<p class="text-secondary text-12 mb-2 text-left">
								- A VIN is made up of
								<span class="font-bold">17 characters</span>
							</p>
						</template>
					</Tooltip>
				</template>
			</AppInput>
		</form>

		<form
			v-if="
				(formType === 'lp' && !makeHidden) || (makeHidden && mainPage && formType === 'lp')
			"
			class="flex flex-col"
			action="#"
			@submit.prevent
		>
			<input
				v-model="form.plate"
				class="app-input mb-3 mt-1 grow"
				:class="{ 'border-error bg-error/10': validate && !isPlateValid }"
				maxlength="9"
				name="plate"
				placeholder="Plate number"
				@input="onInputPlate"
				@keydown.enter="onSubmit"
			/>

			<div class="app-input-wrapper">
				<div
					class="text-12 text-main-400 bold z-2 absolute left-8 top-[13px] flex items-center font-bold md:text-base lg:top-4"
				>
					<span>STATE</span>
					<span class="ml-0 opacity-20 lg:ml-2">
						<img :src="getAssetUrl('assets/line.png')" alt="line" />
					</span>
				</div>
				<select
					v-model="form.state"
					class="app-input app-input-select mb-4 grow pl-24 lg:pl-[120px]"
					:class="{ 'border-error bg-error/10': validate && !isStateValid }"
					name="state"
				>
					<option
						v-for="st in form.states"
						:key="st.label"
						:value="st.label"
						:disabled="st.label === 'STATE:'"
					>
						{{ st.name }}
					</option>
				</select>
				<div
					class="text-main-400 pointer-events-none absolute top-5 right-8 h-1 w-1 scale-y-50 lg:top-6"
				>
					▼
				</div>
			</div>
		</form>

		<!--		<span v-if="errorMessage" class="text-error text-lg mb-auto">-->
		<!--			{{ errorMessage }}-->
		<!--		</span>-->

		<form
			v-if="makeHidden && !mainPage && !inHeader"
			class="flex flex-col"
			action="#"
			@submit.prevent
		>
			<AppInput
				v-model="form.vin"
				v-maska="vinMask"
				class=""
				:input-class="
					validate && !isVinValid && !makeHidden ? 'bg-error/10 border-error' : ''
				"
				name="VIN"
				placeholder="Enter your VIN"
				@input="onInputVin"
				@submit="onSubmit"
			>
				<template #append>
					<Tooltip
						class="xs:-right-3 absolute -top-1 -right-2 h-6 w-6"
						text-class="text-secondary -translate-y-[56%]"
						position="left"
					>
						<template #default>
							<HelpSvg class="fill-secondary" />
						</template>

						<template #content>
							<p class="text-secondary text-12 text-left font-bold">
								Where can I find my VIN?
							</p>

							<p class="text-secondary text-12 mb-2 text-left">
								You can find your VIN in the vehicle registration, title, or your
								insurance card. The VIN number can also be found on the inside of
								the driver’s door or on the driver’s side of the dashboard.
							</p>

							<p class="text-secondary text-12 mb-2 text-left">
								- A VIN cannot include letters
								<span class="font-bold">I, O, Q</span>
							</p>

							<p class="text-secondary text-12 mb-2 text-left">
								- A VIN is made up of
								<span class="font-bold">17 characters</span>
							</p>
						</template>
					</Tooltip>
				</template>
			</AppInput>

			<div
				class="my-6 mx-[auto] flex h-px w-[95%] w-full items-center justify-center bg-gray-300"
			>
				<span class="text-success-base bg-[#ffffff] px-6 py-2 font-medium">OR</span>
			</div>

			<input
				v-model="form.plate"
				class="app-input mb-3 grow"
				:class="{
					'border-error bg-error/10': validate && !isPlateValid && !makeHidden,
				}"
				maxlength="9"
				name="plate"
				placeholder="Plate number"
				@input="onInputPlate"
			/>

			<div class="app-input-wrapper">
				<div
					class="text-12 text-main-400 bold z-2 absolute left-8 top-[13px] flex items-center font-bold md:text-base lg:top-4"
				>
					<span>STATE</span>
					<span class="ml-0 opacity-20 lg:ml-2">
						<img :src="getAssetUrl('assets/line.png')" alt="line" />
					</span>
				</div>
				<select
					v-model="form.state"
					class="app-input app-input-select mb-3 grow pl-24 lg:pl-[120px]"
					:class="{ 'border-error bg-error/10': validate && !isStateValid }"
					name="state"
				>
					<option
						v-for="st in form.states"
						:key="st.label"
						:value="st.label"
						:disabled="st.label === 'STATE:'"
					>
						{{ st.name }}
					</option>
				</select>
				<div
					class="text-main-400 pointer-events-none absolute top-5 right-8 h-1 w-1 scale-y-50 lg:top-6"
				>
					▼
				</div>
			</div>
		</form>

		<form
			v-if="inHeader"
			class="relative flex h-full flex-col bg-transparent py-2 lg:px-2"
			@submit.prevent
		>
			<div class="flex h-full flex-col justify-between">
				<div>
					<div class="relative">
						<AppInput
							v-model="form.vin"
							v-maska="vinMask"
							class=""
							:input-class="
								validate && !isVinValid && !makeHidden
									? 'bg-error/10 border-error'
									: ''
							"
							placeholder="ENTER VIN NUMBER"
							name="VIN"
							@input="onInputVin"
						/>

						<div
							class="text-main-400 absolute left-6 -top-2 bg-white px-1 text-[12px] font-bold lg:left-10 lg:px-3"
						>
							VIN NUMBER
						</div>
					</div>

					<div
						class="my-9 mx-[auto] flex h-px w-[83%] items-center justify-center bg-[#DBDBDB] px-4 lg:my-6"
					>
						<span
							class="text-main-400 bg-[#ffffff] px-2 py-2 text-[12px] font-bold lg:px-6 lg:text-[14px] lg:font-medium"
						>
							OR
						</span>
					</div>

					<div class="mb-2 flex justify-between lg:mb-4">
						<div class="relative w-2/3">
							<input
								v-model="form.plate"
								class="app-input w-[95%] grow"
								:class="{
									'border-error bg-error/10':
										validate && !isPlateValid && !makeHidden,
								}"
								maxlength="9"
								name="plate"
								:placeholder="grid.lg ? 'ENTER LICENSE PLATE' : 'PLATE NUMBER'"
								@input="onInputPlate"
							/>
							<div
								class="text-main-400 absolute left-6 -top-2 bg-white px-1 text-[12px] font-bold lg:left-10 lg:px-3"
							>
								LICENSE PLATE
							</div>
						</div>

						<div class="app-input-wrapper relative mb-3 w-1/3">
							<select
								v-model="form.state"
								class="app-input app-input-select grow"
								:class="{ 'border-error bg-error/10': validate && !isStateValid }"
								name="state"
							>
								<option
									v-for="st in form.states"
									:key="st.label"
									:value="st.label"
									:disabled="st.label === 'STATE:'"
								>
									{{ st.label }}
								</option>
							</select>
							<div
								class="text-main-400 absolute left-4 -top-2 bg-white px-1 text-[12px] font-bold lg:left-5 lg:px-3"
							>
								STATE
							</div>
							<div
								class="text-main-400 pointer-events-none absolute top-5 right-8 h-1 w-1 scale-y-50 lg:top-6"
							>
								▼
							</div>
						</div>
					</div>
				</div>

				<AppButton
					class="mx-auto"
					:class="{ 'w-full lg:w-[auto]': inHeader }"
					cta
					size="Large"
					:disabled="!isPlateValid && !isVinValid"
					@click="onSubmitInHeader"
				>
					<span v-if="!isProcessing" class="h-6">Continue</span>

					<SpinnerSvg v-else class="fill-white" />
				</AppButton>
			</div>
		</form>

		<div v-if="!props.withoutControl && !inHeader" class="tall:xl:mt-9 mt-4">
			<AppButton
				class="mx-auto mb-3"
				:class="{ 'w-full lg:w-[auto]': inHeader }"
				size="Large"
				cta
				:disabled="inHeader ? !isPlateValid && !isVinValid : isProcessing"
				@click="inHeader ? onSubmitInHeader() : onSubmit()"
			>
				<span v-if="!isProcessing" class="h-6">
					{{ inHeader ? 'Continue' : config.getConfigContent('ctaText', 'Get started') }}
				</span>

				<SpinnerSvg v-else class="fill-white" />
			</AppButton>

			<LogoPower
				v-if="!inHeader"
				class="mx-auto mt-3 h-8 w-16 lg:mt-4 lg:h-16 lg:w-24"
				clickable
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, onMounted, ref } from 'vue'
	import { Actions, Steps, Trim, useFormStore, Validations } from '../../../stores/form'
	import AppButton from '../../common/AppButton.vue'
	import { getAssetUrl, vinMask } from '../../../services/helpers'
	import SpinnerSvg from '../components/icons/SpinnerSvg.vue'
	import { useDialogStore } from '../../../stores/dialog'
	import LogoPower from '../../common/LogoPowerSvg.vue'
	import AppInput from '../../common/AppInput.vue'
	import HelpSvg from '../components/icons/HelpSvg.vue'
	import Tooltip from '../../common/Tooltip.vue'
	import { useConfigStore } from '../../../stores/config'
	import { useAppScreen } from '../../../services/app-screen'

	type FormType = 'ymm' | 'vin' | 'lp'

	const config = useConfigStore()
	const form = useFormStore()
	const dialogs = useDialogStore()
	const { grid } = useAppScreen()

	const props = withDefaults(
		defineProps<{
			withoutControl?: boolean
			mainPage?: boolean
			ymmHidden?: boolean
			inHeader?: boolean
		}>(),
		{
			withoutControl: false,
			mainPage: false,
			ymmHidden: false,
			inHeader: false,
		},
	)

	const makeHidden = computed(
		() => config.getConfigContent('makeHidden', false) || props.ymmHidden,
	)
	const formType = ref<FormType>('ymm')
	const isProcessing = ref(false)
	const validate = ref(false)

	const errorMessage = ref('')

	// emits
	defineEmits(['closeForm', 'formModalToggle'])

	onMounted(() => {
		makeHidden.value ? (formType.value = 'vin') : (formType.value = 'ymm')
	})

	const isMakeDisabled = computed(
		() => !form.year || form.year === 'Year' || form.getMakes.length < 2,
	)
	const isModelDisabled = computed(
		() => !form.make || form.make === 'Make' || form.getModels.length < 2,
	)

	const isYearValid = computed(() => form[Validations.IsYearValid])
	const isMakeValid = computed(() => form[Validations.IsMakeValid])
	const isModelValid = computed(() => form[Validations.IsModelValid])
	const isVinValid = computed(() => form[Validations.IsVinValid])
	const isPlateValid = computed(() => form[Validations.IsPlateValid])
	const isStateValid = computed(() => form[Validations.IsStateValid])

	const onChangeTab = (tab: FormType): void => {
		validate.value = false
		errorMessage.value = ''
		formType.value = tab
	}

	const onChangeYear = (event: Event): void => {
		form.vinOrLPType = false
		validate.value = false
		const target = event.target as HTMLInputElement
		target && form.changeYear(target?.value).catch(() => form.resetForm(Steps.AboutYourRide))
	}

	const onChangeMake = (event: Event): void => {
		form.vinOrLPType = false
		validate.value = false
		const target = event.target as HTMLInputElement
		target && form.changeMake(target?.value).catch(() => form.resetForm(Steps.AboutYourRide))
	}

	const onChangeModel = (event: Event): void => {
		form.vinOrLPType = false
		validate.value = false
		const target = event.target as HTMLInputElement

		if (target) {
			isProcessing.value = true
			form.changeModel(target?.value)
				.catch(() => form.resetForm(Steps.AboutYourRide))
				.finally(() => (isProcessing.value = false))
		}
	}

	const onInputVin = (): void => {
		if (!props.inHeader) {
			validate.value = false
			form.plate = ''
			form.year = 'Year'
			form.make = 'Make'
			form.model = 'Model'
			form.styles = []
			form.trims = []
			form.trim = {} as Trim
			form.setStep(Steps.AboutYourRide)
		} else {
			form.plate = ''
			form.state = config.config.location.state
		}
	}

	const onInputPlate = (): void => {
		if (!props.inHeader) {
			validate.value = false
			form.vin = ''
			form.year = 'Year'
			form.make = 'Make'
			form.model = 'Model'
			form.styles = []
			form.trims = []
			form.trim = {} as Trim
			form.setStep(Steps.AboutYourRide)
		} else {
			form.vin = ''
		}
	}

	const onSubmit = (): void => {
		validate.value = true

		if (
			form.currentStep.id !== Steps.AboutYourRide &&
			form.currentStep.id !== Steps.Trims &&
			form.trims.length
		) {
			dialogs.vehicleOpen = true
			return
		}

		if (isProcessing.value || !form[Validations.IsAboutYourRideValid]) {
			if (formType.value === 'ymm' && !form[Validations.IsYMMValid]) {
				dialogs.openErrorModal({
					type: 'ymm',
					title: 'Please Enter Make & Model.',
					subtitle: '',
				})
			}

			if (formType.value === 'vin' && !form[Validations.IsVinValid]) {
				dialogs.openErrorModal({
					type: 'vin',
					title: 'Please Enter a Valid VIN Number.',
					subtitle: '',
				})
			}

			if (
				formType.value === 'lp' &&
				(!form[Validations.IsPlateValid] || !form[Validations.IsStateValid])
			) {
				dialogs.openErrorModal({
					type: 'lp',
					title: 'Please Enter a Valid License Plate and State.',
					subtitle: '',
				})
			}

			return
		}

		isProcessing.value = true
		errorMessage.value = ''

		form[Actions.SubmitAboutYourRideForm]()
			.then(() => {
				form.setStep(Steps.Trims)
				dialogs.vehicleOpen = true
				;(document.activeElement as HTMLElement).blur()
			})
			.catch(e => {
				errorMessage.value = e.code

				if (e?.code === 'forbidden_make') {
					form.setStep(Steps.Rejected)
					dialogs.vehicleOpen = true
				}
			})
			.finally(() => {
				isProcessing.value = false
				validate.value = false
			})
	}

	const onSubmitInHeader = (): void => {
		console.log('SUBMIT')

		validate.value = true

		if (isProcessing.value || !form[Validations.IsAboutYourRideValid]) {
			if (formType.value === 'vin' && !form[Validations.IsVinValid]) {
				dialogs.closeHeaderFormModal()
				dialogs.openErrorModal({
					type: 'vin',
					title: 'Please Enter a Valid VIN Number.',
					subtitle: '',
				})
			}

			if (
				formType.value === 'lp' &&
				(!form[Validations.IsPlateValid] || !form[Validations.IsStateValid])
			) {
				dialogs.closeHeaderFormModal()
				dialogs.openErrorModal({
					type: 'lp',
					title: 'Please Enter a Valid License Plate and State.',
					subtitle: '',
				})
			}
			return
		}

		form.modalVehicleData.plate = form.plate
		form.modalVehicleData.state = form.state

		isProcessing.value = true
		errorMessage.value = ''

		form[Actions.SubmitAboutYourRideFormModal]()
			.then(() => {
				if (form.step !== 1) {
					dialogs.openTrimsModal()
				}

				dialogs.closeHeaderFormModal()

				dialogs.vehicleOpen = true
			})
			.catch(e => {
				// TODO refactor !!!! need to create separate component for "in header form"
				//  store local vin and plate and use it only when validation proceed
				form.plate = ''
				form.vin = ''
				errorMessage.value = e.code
			})
			.finally(() => {
				isProcessing.value = false
				validate.value = false
			})
	}
</script>
